import React from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { omit, startCase } from "lodash";
import {
    useCreateDashboardStore,
    ColorPicker,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from "..";
import DashboardCreateSelectActuatorModal from "../modal/SelectDevices";
import CreateChartModal from "../modal/CreateChart";
import { COLORS } from "constant";
import { ChartSource, ChartType } from "constant/chart";
import { ColorPickerBackground } from "components/common";
import { DeviceSelected } from "types";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const ActuatorCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget name",
        },
        {
            key: "sensorName",
            name: "Actuator",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: LDSU_UUID_LABEL,
        },
        {
            key: "color",
            name: "Color",
            render: (data: any) => (
                <div className="color-picker__toggle">
                    <ColorPickerBackground $inputColor={data.color} />
                </div>
            ),
        },
    ],
    transform: (data: any) => {
        const time_series = getMQTTQueryByDevice(data);

        return {
            attributes: [
                {
                    gateway_id: data.gatewayId,
                    device_id: data.ldsuUID,
                    said: data.said,
                    time_series: time_series,
                    name: data.sensorType,
                    color: data.color,
                    unit: data.unit === "BINARY" ? "" : data.unit,
                },
            ],
            source: ChartSource.SINGLE_ACTUATOR,
            name: data.chartName || "Single Actuator Chart",
            chart: ChartType.SINGLE_LINE,
        };
    },
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.deviceId}.chartName"]`,
            isValid: CHART_NAME_PATTERN.test(item.chartName?.trim() || ""),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

interface SelectedDevicesMap {
    [deviceId: string]: DeviceSelected;
}

const DashboardCreateSingleActuatorComponent: React.FunctionComponent = () => {
    const [showSelectModal, setShowSelectModal] = React.useState(false);
    const [selectedDevices, setSelectedDevices] =
        React.useState<SelectedDevicesMap>({});
    const { createModalShow, setCreateModalShow } = useCreateDashboardStore();

    // Update store data whenever selectedDevices changes
    React.useEffect(() => {
        const deviceList = Object.values(selectedDevices);

        useCreateDashboardStore.setState({
            createData:
                deviceList.length > 0
                    ? {
                        ...ActuatorCreateData,
                        data: deviceList,
                    }
                    : undefined,
        });
    }, [selectedDevices]);

    const onRemove = React.useCallback((device: DeviceSelected) => {
        setSelectedDevices((prev) => omit(prev, device.deviceId));
    }, []);

    const onFieldChange = React.useCallback(
        (
            deviceId: string,
            field: string,
            value: string | [number, string][]
        ) => {
            setSelectedDevices((prev) => ({
                ...prev,
                [deviceId]: {
                    ...prev[deviceId],
                    [field]: value,
                },
            }));
        },
        []
    );

    const onDevicesSelected = React.useCallback(
        (_event: any, data: SelectedDevicesMap) => {
            // Initialize devices with default values
            const initializedDevices: SelectedDevicesMap = {};
            Object.values(data).forEach((device, index) => {
                initializedDevices[device.deviceId] = {
                    ...device,
                    chartName: device.sensorName,
                    color: COLORS[index % COLORS.length] || COLORS[0],
                };
            });
            setSelectedDevices(initializedDevices);
        },
        []
    );

    return (
        <React.Fragment>
            <Row>
                <Col sm="12">
                    <Button
                        className="mt-1 mb-2 d-block"
                        variant="primary"
                        onClick={() => setShowSelectModal(true)}
                    >
                        Select Actuators
                    </Button>
                    <small className="small-text text-primary-blue-4">
                        This widget will support bulk creation. Select multiple
                        actuators to create a separate widget for each.
                    </small>
                    <DashboardCreateSelectActuatorModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        onSave={onDevicesSelected}
                        deviceType="Actuator"
                    />
                </Col>
            </Row>

            <Row className="mt-4 cstm-table mobile-chart-info">
                <div id="chart-edit-form">
                    {Object.values(selectedDevices).map((device) => (
                        <div
                            key={device.deviceId}
                            id={device.deviceId}
                            className="table-row gateway-chart-info"
                        >
                            <div className="d-flex justify-content-end">
                                <CloseOutlined
                                    className="cursor-pointer text-primary-red-1"
                                    onClick={() => onRemove(device)}
                                    onKeyDown={() => onRemove(device)}
                                />
                            </div>
                            <Form.Group>
                                <Form.Label>Widget Name:</Form.Label>
                                <Form.Control
                                    name={`${device.deviceId}.chartName`}
                                    type="text"
                                    value={device.chartName ?? ""}
                                    placeholder="Widget Name"
                                    onChange={(event) =>
                                        onFieldChange(
                                            device.deviceId,
                                            "chartName",
                                            event.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                            <div className="field-group">
                                <p>Actuator: </p>
                                <p>{device.sensorName}</p>
                            </div>
                            <div className="field-group">
                                <p>Type: </p>
                                <p>{startCase(device.sensorType)}</p>
                            </div>
                            <div className="field-group">
                                <p>Gateway: </p>
                                <p>{device.gatewayName}</p>
                            </div>
                            <div className="field-group">
                                <p>LDSU UUID: </p>
                                <p>{device.ldsuUID}</p>
                            </div>

                            <Form.Group className="field-group">
                                <Form.Label>Color:</Form.Label>
                                <ColorPicker
                                    defaultValue={
                                        typeof device.color === "string"
                                            ? device.color
                                            : undefined
                                    }
                                    onChange={(_, color) =>
                                        onFieldChange(
                                            device.deviceId,
                                            "color",
                                            color
                                        )
                                    }
                                />
                            </Form.Group>
                        </div>
                    ))}
                </div>
            </Row>

            <Row className="mt-4 cstm-table desktop-chart-info">
                <Col sm={12}>
                    <Form id="chart-edit-form">
                        {!!Object.values(selectedDevices).length && (
                            <div className="table-head" key="head">
                                <Row className="no-checkbox">
                                    <Col>Actuator</Col>
                                    <Col>Widget name</Col>
                                    <Col>Type</Col>
                                    <Col>Gateway</Col>
                                    <Col>LDSU UUID</Col>
                                    <Col>Color</Col>
                                    <Col md={{ span: 1 }}></Col>
                                </Row>
                            </div>
                        )}
                        {Object.values(selectedDevices).map((device) => (
                            <div
                                key={device.deviceId}
                                id={device.deviceId}
                                className="table-row"
                            >
                                <Row className="no-checkbox align-items-center">
                                    <Col className="text-break">
                                        {device.sensorName}
                                    </Col>
                                    <Col>
                                        <Form.Control
                                            name={`${device.deviceId}.chartName`}
                                            type="text"
                                            value={device.chartName ?? ""}
                                            placeholder="Widget Name"
                                            onChange={(event) =>
                                                onFieldChange(
                                                    device.deviceId,
                                                    "chartName",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col className="text-break">
                                        {startCase(device.sensorType)}
                                    </Col>
                                    <Col className="text-break">
                                        {device.gatewayName}
                                    </Col>
                                    <Col>{device.ldsuUID}</Col>
                                    <Col>
                                        <ColorPicker
                                            defaultValue={
                                                typeof device.color === "string"
                                                    ? device.color
                                                    : undefined
                                            }
                                            onChange={(_, color) =>
                                                onFieldChange(
                                                    device.deviceId,
                                                    "color",
                                                    color
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col
                                        md={{ span: 1 }}
                                        className="d-flex justify-content-end"
                                    >
                                        <CloseOutlined
                                            className="cursor-pointer mr-2 text-primary-red-1"
                                            onClick={() => onRemove(device)}
                                            onKeyDown={() => onRemove(device)}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </Form>
                </Col>
            </Row>
            <CreateChartModal
                show={
                    createModalShow && Object.keys(selectedDevices).length > 0
                }
                onHide={() => setCreateModalShow(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(DashboardCreateSingleActuatorComponent);
